import { useTranslation } from 'react-i18next'

import {
  Button,
  EditIcon,
  MonetizationIcon,
  Skeleton,
  Tag,
  Text,
  Tooltip,
  TrashIcon,
} from '@mercai/clever'

import { TransactionInfoProps } from './transaction-info.type'

import styles from './transaction-info.module.css'
import { formatCurrency, formatLongDate } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { Link } from 'react-router-dom'
import { FileInfo } from '@/components'

export const TransactionInfo = ({
  isLoading,
  transaction,
  onDelete,
  onUpdate,
  orderReference,
}: TransactionInfoProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenOrderCenterPage')

  const mxnValue = +(transaction?.currency === 'MXN'
    ? transaction?.value
    : +(transaction?.value || 0) * +(transaction?.dollarExchangeRate || 0))

  const PaymentStatus = () => {
    const { t } = useTranslation('screenOrderCenterPage')

    if (transaction?.status === 'processed') {
      return (
        <Link
          to={`${ROUTES_ADMIN.ORDER_CENTER.FINANCE.fullPath()}?search=${orderReference}`}
          target="_blank"
        >
          <Tag variant="success" model="light">
            #{orderReference}
          </Tag>
        </Link>
      )
    }

    return (
      <Tag variant="danger" model="light">
        {t('orderCenterShowTabClientStatePending')}
      </Tag>
    )
  }

  const Payer = () => {
    const buyerName = transaction?.payer?.usersNames
      ? transaction?.payer?.usersNames?.join(', ')
      : ''
    const companyName = transaction?.payer?.companyName

    const uuid = transaction?.payer?.uuid

    if (isLoading) {
      return (
        <div className={styles.buyer}>
          <div className={styles['buyer-content']}>
            <Text variant="title-base">
              <Skeleton width="7rem" height="1.3rem" />
            </Text>

            <Text variant="text-small">
              <Skeleton width="6rem" height="1rem" />
            </Text>
          </div>
        </div>
      )
    }

    if (!uuid) {
      return (
        <div className={styles.buyer}>
          <div className={styles['buyer-content']}>
            <Text variant="title-base">
              {t('orderCenterShowTabClientClientNameNullLabel')}
            </Text>

            <Text variant="text-small">
              ({t('orderCenterShowTabClientClientNameNullLabel')})
            </Text>
          </div>
        </div>
      )
    }

    return (
      <Link
        target="_blank"
        to={uuid ? ROUTES_ADMIN.PROFILE.SHOW.fullPath(uuid) : ''}
        className={styles.buyer}
      >
        <div className={styles['buyer-content']}>
          <Text variant="title-base">{companyName || '-'}</Text>

          {buyerName && <Text variant="text-small">({buyerName})</Text>}
        </div>
      </Link>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles['header-title']}>
            <Skeleton width="1.5rem" height="1.5rem" />
            <Text variant="title-base">
              <Skeleton width="6rem" height="1.5rem" />
            </Text>
            <Text variant="title-base" className={styles['header-title-date']}>
              <Skeleton width="6rem" height="1.5rem" />
            </Text>
          </div>
          <div className={styles['header-actions']}>
            <Skeleton width="2.5rem" height="2.5rem" />
            <Skeleton width="2.5rem" height="2.5rem" />
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.column}>
            <div>
              <Text variant="text-base">
                <Skeleton width="3.5rem" height="1.5rem" />
              </Text>
              <div className={styles.prices}>
                <Text variant="text-large">
                  <Skeleton width="8rem" height="1.8rem" />
                </Text>
                <Text variant="title-base">
                  <Skeleton width="6rem" height="1.5rem" />
                </Text>
              </div>
            </div>
            <div className={styles.row}>
              <Text variant="text-base">
                <Skeleton width="4.5rem" height="1.5rem" />
              </Text>
              <Skeleton width="8rem" height="1.8rem" />
            </div>
            <div className={styles.row}>
              <Text variant="text-base">
                <Skeleton width="4.5rem" height="1.5rem" />
              </Text>
              <Text variant="text-base">
                <Skeleton width="8rem" height="1.5rem" />
              </Text>
            </div>
            <div className={styles.row}>
              <Text variant="text-base">
                <Skeleton width="5.5rem" height="1.5rem" />
              </Text>
              <Text variant="text-base">
                <Skeleton width="9rem" height="1.5rem" />
              </Text>
            </div>
            <div className={styles['row-align-start']}>
              <Text variant="text-base">
                <Skeleton width="5rem" height="1.5rem" />
              </Text>
              <Payer />
            </div>
          </div>

          <div className={styles.column}>
            <Skeleton width="12rem" height="2.5rem" />
            <Skeleton width="12rem" height="2.5rem" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles['header-title']}>
          <MonetizationIcon />
          <Text variant="title-base">
            {t('orderCenterShowTabClientDepositTitle')}
          </Text>
          <Text variant="title-small" className={styles['header-title-date']}>
            -
          </Text>
          <Text variant="title-small" className={styles['header-title-date']}>
            {transaction?.paidAt ? formatLongDate(transaction?.paidAt) : ''}
          </Text>
        </div>
        <div className={styles['header-actions']}>
          {onDelete ? (
            <Button variant="secondary" onClick={onDelete} model="square">
              <TrashIcon />
            </Button>
          ) : (
            <Tooltip
              orientation="left"
              text={t('orderCenterShowTabClientDeleteTransactionNotPermission')}
            >
              <Button variant="secondary" model="square" disabled>
                <TrashIcon />
              </Button>
            </Tooltip>
          )}
          <Button variant="secondary" onClick={onUpdate} model="square">
            <EditIcon />
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.column}>
          <div>
            <Text variant="text-base">
              <strong>{t('orderCenterShowTabClientAmountLabel')}:</strong>
            </Text>
            <div className={styles.prices}>
              <Text variant="text-large">
                <strong>{formatCurrency(mxnValue, 'es-MX', 'MXN')}</strong>
              </Text>
              <Text variant="title-base">-</Text>
              <Text variant="title-base">
                {formatCurrency(
                  +(transaction?.dollarValue || 0),
                  'es-MX',
                  'USD',
                )}
              </Text>
            </div>
          </div>
          <div className={styles.row}>
            <Text variant="text-base">
              <strong>{t('orderCenterShowTabClientStatusLabel')}:</strong>
            </Text>
            <PaymentStatus />
          </div>
          <div className={styles.row}>
            <Text variant="text-base">
              <strong>
                {t('orderCenterShowTabClientPaymentMethodLabel')}:
              </strong>
            </Text>
            <Text variant="text-base">
              {tEnums(
                `transaction_payment_method_${transaction?.paymentMethod}`,
              )}
            </Text>
            <Text variant="text-base">({transaction?.destinationBank})</Text>
          </div>
          <div className={styles['row-align-start']}>
            <Text variant="text-base">
              <strong>{t('orderCenterShowTabClientClientLabel')}:</strong>
            </Text>
            <Payer />
          </div>
        </div>

        <div className={styles.column}>
          {transaction?.proof && (
            <FileInfo
              className={styles.proof}
              isLoading={isLoading}
              file={transaction.proof}
              title={t('orderCenterShowTabClientProofTitle')}
            />
          )}
          {transaction?.paymentReceipt && (
            <FileInfo
              className={styles.proof}
              isLoading={isLoading}
              file={transaction.paymentReceipt}
              title={t('orderCenterShowTabClientReceiptTitle')}
            />
          )}
        </div>
      </div>
    </div>
  )
}
